import React, { FunctionComponent, useState, useEffect } from "react"
import { Button, Checkbox, Form, Input, InputNumber, notification, Select } from "antd"
import { ScreenersService, BrokersService, MarketsService } from "../api/services.gen"
import { useNavigate, useSearchParams } from "react-router"

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 12 },
};
const tailLayout = {
  wrapperCol: { offset: 6, span: 16 },
};

const OptionInterestScreenerCreatePage: FunctionComponent = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const onFinish = async (values: any) => {
    try {
      // Save form values to query params using replace (so it doesn't add to history)
      const params = new URLSearchParams()
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            params.set(key, JSON.stringify(value))
          } else if (typeof value === 'boolean') {
            params.set(key, value.toString())
          } else {
            params.set(key, value.toString())
          }
        }
      })
      navigate(`?${params.toString()}`, { replace: true })
      
      const response = await ScreenersService.createOptionInterestScreeningRun({
        requestBody: {
          broker: values.broker,
          currency: values.currency,
          budget: values.budget,
          markets: values.markets,
          withMonthlyOptions: values.withMonthlyOptions,
          valuationMin: values.valuationMin,
          incomeMin: values.incomeMin,
          priceMin: values.priceMin,
          priceMax: values.priceMax,
        }
      })
      
      notification.success({
        message: 'Screening started',
        description: 'The screening run has been initiated'
      })
      
      // Navigate to results page with run ID
      navigate(`/screeners/option-interest/run/${response.runId}`)
      
    } catch (e: any) {
      console.error(e)
      notification.error({
        message: 'Error',
        description: e.toString()
      })
    }
  }

  return <>
    <h2>Option Interest Screener</h2>
    <p>
      Screen for option writing opportunities based on valuation, income, and market criteria.
    </p>
    <br /><br />
    <OptionInterestScreenerForm onFinish={onFinish} searchParams={searchParams} />
  </>
}

const OptionInterestScreenerForm = ({ onFinish, searchParams }: { onFinish: (values: any) => void, searchParams: URLSearchParams }) => {
  const [form] = Form.useForm()
  const [brokers, setBrokers] = useState<string[]>([])
  const [markets, setMarkets] = useState<string[]>([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const brokersResponse = await BrokersService.getBrokers()
        setBrokers(brokersResponse.brokers.map((b) => b.name))

        const marketsResponse = await MarketsService.listMarkets()
        setMarkets(marketsResponse.markets.map((m) => m.id))
      } catch (e) {
        console.error('Failed to fetch data:', e)
      }
    }
    fetchData()
  }, [])
  
  // Parse initial values from query params
  const getInitialValues = () => {
    const defaultValues = {
      currency: 'EUR',
      budget: 10000,
      priceMin: 1,
      priceMax: 1000,
    }
    
    const queryValues: any = {}
    
    // Parse query parameters
    searchParams.forEach((value, key) => {
      if (key === 'markets') {
        try {
          queryValues[key] = JSON.parse(value)
        } catch {
          queryValues[key] = value
        }
      } else if (key === 'withMonthlyOptions') {
        queryValues[key] = value === 'true'
      } else if (['budget', 'valuationMin', 'incomeMin', 'priceMin', 'priceMax'].includes(key)) {
        const parsed = parseFloat(value)
        if (!isNaN(parsed)) {
          queryValues[key] = parsed
        }
      } else {
        queryValues[key] = value
      }
    })
    
    return { ...defaultValues, ...queryValues }
  }
  
  return <Form {...layout} form={form} onFinish={onFinish} initialValues={getInitialValues()}>
      <Form.Item name="broker" label="Broker" extra="Broker used for screening">
        <Select placeholder="Select a broker">
          {brokers.map(broker => (
            <Select.Option key={broker} value={broker}>
              {broker}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="currency" label="Currency" extra="Currency to use for screening">
        <Input placeholder="EUR" />
      </Form.Item>
      <Form.Item name="budget" label="Budget" extra="Maximum budget for screening">
        <InputNumber placeholder="10000" />
      </Form.Item>
      <Form.Item name="markets" label="Markets" extra="Markets to screen (can select multiple)">
        <Select mode="multiple" placeholder="Select markets">
          {markets.map(market => (
            <Select.Option key={market} value={market}>
              {market}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="withMonthlyOptions" valuePropName="checked" label="Monthly Options" extra="Include stocks with monthly options">
        <Checkbox />
      </Form.Item>
      <Form.Item name="valuationMin" label="Minimum Valuation" extra="Minimum valuation score">
        <InputNumber placeholder="0" />
      </Form.Item>
      <Form.Item name="incomeMin" label="Minimum Income" extra="Minimum income threshold">
        <InputNumber placeholder="0" />
      </Form.Item>
      <Form.Item name="priceMin" label="Minimum Price" extra="Minimum option price">
        <InputNumber placeholder="1" />
      </Form.Item>
      <Form.Item name="priceMax" label="Maximum Price" extra="Maximum option price">
        <InputNumber placeholder="1000" />
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
}

export { OptionInterestScreenerCreatePage, OptionInterestScreenerForm }
export default OptionInterestScreenerCreatePage
